package diana.soleil.design.iraniancanadienbazzar.navigation

import kotlinx.serialization.Serializable

@Serializable
sealed interface Route {
    @Serializable object Onboarding : Route
    @Serializable object Welcome : Route
    @Serializable object SignIn : Route
    @Serializable object SignUp : Route
    @Serializable object Main : Route
    @Serializable object Categories : Route
    /** [query] pre-fills the search field (e.g. a recipe's grocery deep link); null = blank. */
    @Serializable data class Search(val query: String? = null) : Route
    @Serializable object Map : Route
    @Serializable object Gems : Route
    /** [focusJokeId] opens the reader on that joke (daily-gem push / bell row); null = first joke. */
    @Serializable data class GemsJokes(val focusJokeId: String? = null) : Route
    /** [focusFactId] opens the reader on that fact (daily-gem push / bell row); null = first fact. */
    @Serializable data class GemsDanestaniha(val focusFactId: String? = null) : Route
    @Serializable object GemsShahnameh : Route
    @Serializable data class GemsTaleDetail(val taleId: String) : Route
    /** Persian poetry: the shelf of poets. */
    @Serializable object GemsPoetry : Route
    /** One poet's poems. */
    @Serializable data class GemsPoet(val poetId: String) : Route
    @Serializable data class GemsPoemDetail(val poemId: String) : Route
    /** Regional short stories. */
    @Serializable object GemsStories : Route
    /** Fal-e Hafez: draw a random Hafez ghazal that carries a reading. */
    @Serializable object GemsFal : Route
    @Serializable data class GemsStoryDetail(val storyId: String) : Route
    @Serializable object GemsRecipes : Route
    @Serializable object AddBusiness : Route
    @Serializable object Admin : Route
    @Serializable object Messages : Route
    /**
     * Events browser. Events gave up its bottom-nav slot to Messages (2026-08-02), so it is a pushed
     * route now, entered from the Home Events card; `EventDetail` deep links are unaffected.
     */
    @Serializable object Events : Route
    @Serializable object Dashboard : Route
    /** Owner editor for one listing (details, photos, and admin-approved name/category/address). */
    @Serializable data class EditListing(val businessId: String) : Route
    @Serializable object Cart : Route
    @Serializable object Checkout : Route
    @Serializable object OrderHistory : Route
    @Serializable object MyTickets : Route
    @Serializable object Notifications : Route
    @Serializable object BlockedUsers : Route
    @Serializable object Terms : Route
    /** Preferences, split out of the Profile tab 2026-08-02 so Profile can be an actual profile. */
    @Serializable object Settings : Route
    /**
     * Saved businesses + RSVP'd events + bookmarked news. Added 2026-08-03: RSVPs were written and
     * never readable back, and news had no save action at all.
     */
    @Serializable object Saved : Route

    @Serializable data class BusinessList(val categoryId: String) : Route
    @Serializable data class BusinessDetail(val businessId: String) : Route
    @Serializable data class WriteReview(val businessId: String) : Route
    @Serializable data class AllReviews(val businessId: String) : Route
    @Serializable data class AllPhotos(val businessId: String) : Route
    @Serializable object NewsList : Route
    @Serializable data class NewsDetail(val articleId: String) : Route
    @Serializable data class ClaimBusiness(val businessId: String) : Route
    @Serializable data class ManageReviews(val businessId: String) : Route
    @Serializable data class MarketplaceItem(val itemId: String) : Route
    @Serializable data class Conversation(val conversationId: String) : Route
    @Serializable data class ChatWithBusiness(val businessId: String) : Route
    @Serializable object NewMessage : Route
    @Serializable object People : Route
    @Serializable object PeopleMap : Route
    @Serializable data class PersonProfile(val uid: String) : Route
    @Serializable data class Channel(val businessId: String) : Route
    @Serializable data class ProductDetail(val businessId: String, val productId: String) : Route
    @Serializable data class OrderConfirmation(val orderId: String) : Route
    @Serializable data class ManageProducts(val businessId: String) : Route
    @Serializable data class ManageOrders(val businessId: String) : Route
    @Serializable data class ListingAnalytics(val businessId: String) : Route
    @Serializable data class EventDetail(val eventId: String) : Route
    @Serializable data class EventTickets(val eventId: String) : Route
    @Serializable data class TicketIssued(val ticketId: String) : Route
    @Serializable data class GemsRecipeDetail(val recipeId: String) : Route
    @Serializable data class GemsCookMode(val recipeId: String) : Route
}
